import { ref, type Ref } from "vue";
import { useRouter } from "vue-router";
import { useCustomizeWorkflow } from "@/queries/projects";
import type { Project } from "@/types";

// Single decision point for "given this project + workflow id, what workflow do I actually open in
// the builder, and do I need to fork first?" Every builder entry point (Workflows tab, run-history
// "Open in Builder", the builder's own workflow-select dropdown) routes through this so the
// fork-vs-master rules live in one place — no entry point can quietly reintroduce the bypass bug
// where a subscriber lands on the publisher's master workflow un-forked (edits then 403 on save).
//
// The rules:
//   • Non-subscriber project (Master / DIY) → open the workflow id as-is.
//   • Subscriber + already customized       → resolve to the fork (idempotent customize) and open it.
//   • Subscriber + NOT customized           → do NOT fork silently. Confirm-before-forking:
//       - openInBuilder(): opens the base workflow read-only (history view). Looking is free.
//       - the caller (BuilderView) gates the transition *into* the editor: on an edit attempt it
//         calls requestFork(), which pops the shared confirm; on confirm we fork and swap the route.
//
// `projectId` is reactive because the same composable instance is reused as the route changes.

export interface BuilderNavWorkflow {
  /** Original workflow id — for a subscriber this is the publisher's master id (what pins key off). */
  id: string;
  name?: string;
  /** True when a fork/pin already exists for this workflow in the subscriber's project. */
  isCustomized?: boolean;
}

export type BuilderOpenMode = "editor" | "history";

export function useBuilderNavigation(projectId: Ref<string>) {
  const router = useRouter();
  const { customizeWorkflowAsync, customizeWorkflowPending, customizeWorkflowError, customizeWorkflowReset } =
    useCustomizeWorkflow(projectId);

  // Shared confirm-modal state. The caller renders <CustomizeWorkflowModal> bound to these.
  const showCustomizeModal = ref(false);
  const customizeTarget = ref<BuilderNavWorkflow | null>(null);
  // Where to land after a confirmed fork — carried from the triggering call.
  const pendingMode = ref<BuilderOpenMode>("editor");

  function isSubscriber(project: Project | null): boolean {
    return project?.projectType === "Subscriber";
  }

  function builderPath(workflowId: string, mode: BuilderOpenMode): string {
    const base = `/projects/${projectId.value}/builder/${workflowId}`;
    return mode === "history" ? `${base}/history` : base;
  }

  /**
   * Resolve the workflow to open, forking transparently only when a fork already exists, and navigate.
   * For an un-customized subscriber base workflow this opens read-only (history) — it never forks
   * without a confirm; that happens via requestFork() on an edit attempt.
   */
  async function openInBuilder(
    project: Project | null,
    workflow: BuilderNavWorkflow,
    mode: BuilderOpenMode = "editor"
  ): Promise<void> {
    if (!projectId.value) return;

    // Non-subscriber, or an already-customized subscriber workflow that we can resolve idempotently.
    if (isSubscriber(project) && workflow.isCustomized) {
      try {
        const result = await customizeWorkflowAsync(workflow.id);
        await router.push(builderPath(result.forkedWorkflowId, mode));
      } catch {
        // Error surfaced via customizeWorkflowError; leave navigation to the caller's discretion.
      }
      return;
    }

    // Non-subscriber projects: open as-is.
    // Un-customized subscriber base workflow: open read-only — the edit gate handles forking.
    await router.push(builderPath(workflow.id, mode));
  }

  /**
   * Ask to fork an un-customized subscriber base workflow. Opens the shared confirm; on confirm the
   * caller runs confirmFork(). Use this from the edit-transition gate (confirm-before-forking).
   */
  function requestFork(workflow: BuilderNavWorkflow, mode: BuilderOpenMode = "editor"): void {
    customizeWorkflowReset();
    customizeTarget.value = workflow;
    pendingMode.value = mode;
    showCustomizeModal.value = true;
  }

  /** Confirm handler for the shared modal — forks and navigates to the fork in the requested mode. */
  async function confirmFork(): Promise<void> {
    if (!customizeTarget.value || !projectId.value) return;
    try {
      const result = await customizeWorkflowAsync(customizeTarget.value.id);
      showCustomizeModal.value = false;
      const mode = pendingMode.value;
      customizeTarget.value = null;
      await router.push(builderPath(result.forkedWorkflowId, mode));
    } catch {
      // Stays open; error renders in the modal via customizeWorkflowError.
    }
  }

  function cancelFork(): void {
    if (customizeWorkflowPending.value) return;
    showCustomizeModal.value = false;
    customizeTarget.value = null;
  }

  return {
    // navigation
    openInBuilder,
    // confirm-before-forking flow
    requestFork,
    confirmFork,
    cancelFork,
    // modal state to bind to <CustomizeWorkflowModal>
    showCustomizeModal,
    customizeTarget,
    customizeWorkflowPending,
    customizeWorkflowError,
  };
}
